import React, { ReactNode } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';

import Badge from '../Badge/Badge';
import AppStorage from '../Storage/Storage';
import { translate } from '../utils/utils';
import Labels from '../enums/Labels';
import ProgressBarStep from '../interfaces/ProgressBarStep';
import { Location } from 'history';

import ReadSpeakerReader from '../ReadSpeakerReader';
import {
  languageToReadSpeakerLang,
  ReadSpeakerProvider,
} from '../ReadSpeakerReader/ReadSpeakerProvider';

import './progressBar.css';

type Props = {
  navigate: NavigateFunction;
  progressBar: Array<ProgressBarStep>;
  location?: Location;
  specificLabels?: null | string[];
};

type State = {
  navigateComponent: ReactNode;
};

export class ProgressBar extends React.PureComponent<Props, State> {
  private readonly storage: AppStorage;

  private readonly navigate: NavigateFunction;

  private readonly locationSearch: string;

  constructor(props: Props) {
    super(props);
    this.locationSearch = props.location?.search || '';
    this.storage = AppStorage.getInstance();
    this.state = {
      navigateComponent: null,
    };
    this.navigate = props.navigate;
  }

  getPosition = (list: Array<ProgressBarStep>, elem: ProgressBarStep): number =>
    list.indexOf(elem) + 1;

  redirectToPath = (path: string): void => {
    const id = window.location.pathname.split('/')[1];
    setTimeout(
      () => this.navigate(`/${id}/${path}${this.locationSearch || ''}`),
      100,
    );
  };

  getStepLabel = (progressBarStep: ProgressBarStep, index: number): string => {
    const { specificLabels } = this.props;

    if (specificLabels) {
      return specificLabels[index];
    }

    return translate(progressBarStep.name, this.storage.getLocale);
  };

  render(): React.ReactElement {
    const stepLabel = translate(
      Labels.PROGRESS_BAR_STEP,
      this.storage.getLocale,
    );
    const progressBars = this.props.progressBar;
    const lang = this.storage.getLocale;

    return (
      <div className="progress-bar-container">
        <ReadSpeakerReader id="menu-progress-bar" />
        <div
          className="progress-bar"
          id="menu-progress-bar"
          lang={languageToReadSpeakerLang(lang)}
        >
          {progressBars.map((progressBarStep, index) => (
            <Badge
              clickHandler={(): void => {
                this.redirectToPath(progressBarStep.path);
              }}
              key={progressBarStep.step}
              orderNumber={this.getPosition(progressBars, progressBarStep)}
              name={`${stepLabel} ${this.getPosition(progressBars, progressBarStep)}: ${this.getStepLabel(progressBarStep, index)}`}
              step={progressBarStep}
              language={lang}
            />
          ))}
        </div>
      </div>
    );
  }
}

const ProgressBarHoc = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storage = AppStorage.getInstance();
  const progressBars = storage.getProgressBarByUnderwriterOwner();
  const specificLabels = storage.getProductSpecificProgressBarLabels;

  return (
    <ProgressBar
      location={location}
      navigate={navigate}
      progressBar={progressBars}
      specificLabels={specificLabels}
    />
  );
};

export default observer(ProgressBarHoc);
